import "./CreateProjectPanel.css";

import { CloseDarkIcon, RoundedPlusSignIcon, TrashDark } from "../../../../../assets";
import { LANDHOLDING_REFERENCE_TYPE_OPTIONS } from "../../../../../constants";
import { LandholdingDraft, ProjectDraft } from "../../../../../models";
import {
  Autocomplete,
  Button,
  Column,
  Divider,
  FlyoutPanel,
  KanaTooltip,
  RadioButtonGroup,
  Row,
  Select,
  TextInput,
} from "../../../../../widget";
import { useCreateProjectPanel } from "./useCreateProjectPanel";

interface CreateProjectPanelProps {
  show: boolean;
  onClose: () => void;
  onSave: (projects: ProjectDraft[], landholding: LandholdingDraft, isLandholding: boolean) => void;
}

const LANDHOLDING_RADIO_DATA = [
  { key: "yes", value: "Yes" },
  { key: "no", value: "No" },
];

export const CreateProjectPanel = ({ show, onClose, onSave }: CreateProjectPanelProps): JSX.Element | null => {
  const {
    landholdingChoice,
    landholdingDraft,
    projectDetails,
    countryOptions,
    getProjectTypeOptions,
    isHandleSaveLoading,
    getStandardOptions,
    updateLandholdingChoice,
    updateLandholdingField,
    updateProjectField,
    updateProjectTypeField,
    updateStandardField,
    addProjectDetails,
    removeProjectDetails,
    handleSave,
  } = useCreateProjectPanel(onSave, onClose);

  const isLandholding = landholdingChoice === "yes";

  const renderProjectDetails = (index: number, countries: typeof countryOptions): JSX.Element => {
    const details = projectDetails[index];
    const showDelete = isLandholding && projectDetails.length > 1;
    const availableProjectTypeOptions = getProjectTypeOptions(details.standardUuid);
    const standardOptions = getStandardOptions(details.projectTypeUuid);

    return (
      <div key={details.key}>
        {index > 0 && (
          <Row spacingV="ll">
            <Column span={12}>
              <Divider type="horizontal" className="CreateProjectPanelDivider" />
            </Column>
          </Row>
        )}
        <Row spacingV="ll" align="center">
          <Column span={6}>
            <div className="CreateProjectPanelProjectDetailsHeader">
              <h2>Project details</h2>
              {showDelete && (
                <button
                  type="button"
                  className="CreateProjectPanelIconButton"
                  onClick={() => removeProjectDetails(details.key)}
                  aria-label="Remove project"
                >
                  <TrashDark />
                </button>
              )}
            </div>
          </Column>
        </Row>
        <Row spacingV="ml">
          <Column span={6}>
            <TextInput
              label="Project name*"
              name={`project-name-${details.key}`}
              value={details.name}
              onChange={(value) => updateProjectField(details.key, "name", value)}
            />
          </Column>
          <Column span={6}>
            <div className="CreateProjectPanelSelectWithAction">
              <Select
                label="Project type*"
                value={details.projectTypeUuid}
                onChange={(value) => updateProjectTypeField(details.key, value)}
                data={availableProjectTypeOptions}
                placeholder="Select"
                corners="rounded"
                tooltip={
                  <KanaTooltip
                    tooltipHeader=""
                    tooltipText={"Don't see the right project type? Please contact Kana Earth"}
                  />
                }
              />
              {details.projectTypeUuid && (
                <Button
                  variant="quaternary"
                  size="custom"
                  minWidth={false}
                  hasNoPadding
                  className="CreateProjectPanelFieldResetButton"
                  icon={<CloseDarkIcon />}
                  text=""
                  onClick={() => updateProjectTypeField(details.key, "")}
                />
              )}
            </div>
          </Column>
        </Row>
        <Row spacingV="ml">
          <Column span={6}>
            <div className="CreateProjectPanelSelectWithAction">
              <Select
                label="Standard*"
                value={details.standardUuid}
                onChange={(value) => updateStandardField(details.key, value)}
                data={standardOptions}
                placeholder="Select"
                corners="rounded"
              />
              {details.standardUuid && (
                <Button
                  variant="quaternary"
                  size="custom"
                  minWidth={false}
                  hasNoPadding
                  className="CreateProjectPanelFieldResetButton"
                  icon={<CloseDarkIcon />}
                  text=""
                  onClick={() => updateStandardField(details.key, "")}
                />
              )}
            </div>
          </Column>
          <Column span={6}>
            {countries && (
              <Autocomplete
                label="Country"
                initialKey={details.countryCode}
                data={countries}
                onSelect={(value) => updateProjectField(details.key, "countryCode", value)}
                corners="rounded"
              />
            )}
          </Column>
        </Row>
        <Row spacingV="ml">
          <Column span={6}>
            <TextInput
              label="Region / County / State"
              name={`project-region-${details.key}`}
              value={details.region}
              onChange={(value) => updateProjectField(details.key, "region", value)}
              optional
            />
          </Column>
          <Column span={6}>
            <TextInput
              label="Postcode"
              name={`project-postcode-${details.key}`}
              value={details.postcode}
              onChange={(value) => updateProjectField(details.key, "postcode", value)}
              optional
            />
          </Column>
        </Row>
        <Row spacingV="ml">
          <Column span={6}>
            <TextInput
              label="Latitude"
              name={`project-latitude-${details.key}`}
              value={details.latitude}
              onChange={(value) => updateProjectField(details.key, "latitude", value)}
              optional
            />
          </Column>
          <Column span={6}>
            <TextInput
              label="Longitude"
              name={`project-longitude-${details.key}`}
              value={details.longitude}
              onChange={(value) => updateProjectField(details.key, "longitude", value)}
              optional
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={6}>
            <TextInput
              label="Area (ha)"
              name={`project-area-${details.key}`}
              value={details.areaNetHa}
              onChange={(value) => updateProjectField(details.key, "areaNetHa", value)}
              optional
            />
          </Column>
        </Row>
      </div>
    );
  };

  return (
    <FlyoutPanel
      show={show}
      onClose={onClose}
      size="Large"
      header="Add project"
      buttonsChildren={null}
      contentChildren={
        <div className="CreateProjectPanel">
          {/* Landholding creation is intentionally disabled until the api is ready. */}
          <Row spacingV="m">
            <Column span={12}>
              <p className="body1">
                Does this deal relate to a landscape which may be delivering multiple ecosystem interventions, across
                different projects, project types and standards? (i.e. a landholding)
              </p>
            </Column>
          </Row>
          <Row spacingV="ll">
            <Column span={12}>
              <RadioButtonGroup
                data={LANDHOLDING_RADIO_DATA}
                value={landholdingChoice}
                onChange={updateLandholdingChoice}
              />
            </Column>
          </Row>
          {isLandholding && (
            <>
              <Row spacingV="ll">
                <Column span={12}>
                  <h2>Landholding details</h2>
                </Column>
              </Row>
              <Row spacingV="ml">
                <Column span={6}>
                  <TextInput
                    label="Landholding name*"
                    name="landholding-name"
                    value={landholdingDraft.name}
                    onChange={updateLandholdingField("name")}
                  />
                </Column>
                <Column span={6}>
                  <Select
                    label="Landholding reference*"
                    value={landholdingDraft.reference}
                    onChange={updateLandholdingField("reference")}
                    data={LANDHOLDING_REFERENCE_TYPE_OPTIONS}
                    placeholder="Select"
                  />
                </Column>
              </Row>
              <Row spacingV="ml">
                <Column span={6}>
                  <TextInput
                    label="Landholding reference ID*"
                    name="landholding-reference-id"
                    value={landholdingDraft.referenceId}
                    onChange={updateLandholdingField("referenceId")}
                  />
                </Column>
                <Column span={6}>
                  <Select
                    label="Country"
                    value={landholdingDraft.countryCode}
                    onChange={updateLandholdingField("countryCode")}
                    data={countryOptions ?? []}
                    placeholder="Select"
                    corners="rounded"
                  />
                </Column>
              </Row>
              <Row spacingV="ml">
                <Column span={6}>
                  <TextInput
                    label="Region / County / State*"
                    name="landholding-region"
                    value={landholdingDraft.region}
                    onChange={updateLandholdingField("region")}
                  />
                </Column>
              </Row>
              <Row spacingV="ll">
                <Column span={6}>
                  <TextInput
                    label="Latitude"
                    name="landholding-latitude"
                    value={landholdingDraft.latitude}
                    onChange={updateLandholdingField("latitude")}
                    optional
                  />
                </Column>
                <Column span={6}>
                  <TextInput
                    label="Longitude"
                    name="landholding-longitude"
                    value={landholdingDraft.longitude}
                    onChange={updateLandholdingField("longitude")}
                    optional
                  />
                </Column>
              </Row>
            </>
          )}
          {projectDetails.map((_, index) => renderProjectDetails(index, countryOptions))}

          {isLandholding && (
            <Row spacingV="ll">
              <Column span={12}>
                <Button
                  text="Add another project"
                  icon={<RoundedPlusSignIcon />}
                  iconPosition="left"
                  variant="reversedPrimary"
                  onClick={addProjectDetails}
                  hasNoPadding
                  textBold
                />
              </Column>
            </Row>
          )}

          <Row>
            <Column span={12}>
              <Button text="Save" corners="rounded" onClick={handleSave} isLoading={isHandleSaveLoading} />
            </Column>
          </Row>
        </div>
      }
    />
  );
};
